import { CONFIG } from '@/config/environment';

interface RenewedSession { access: string; refresh: string }

export class SessionRefreshError extends Error {
  constructor(message: string, public readonly expired = false) { super(message); }
}

const accessKeys = ['padelmaster_access_token', 'accessToken', 'access_token', 'authToken'];
const refreshKeys = ['padelmaster_refresh_token', 'refreshToken', 'refresh_token'];
const read = (keys: string[]) => keys.map(k => localStorage.getItem(k) || sessionStorage.getItem(k)).find(Boolean);
let inFlight: Promise<RenewedSession> | null = null;

/** Uma renovação por sessão, partilhada pelo interceptor e temporizador.
 * Web Locks coordena também separadores no mesmo browser. Uma resposta tardia
 * nunca volta a iniciar a sessão depois de logout ou troca de conta.
 */
export function refreshSession(): Promise<RenewedSession> {
  if (inFlight) return inFlight;
  const initialRefresh = read(refreshKeys);
  if (!initialRefresh) return Promise.reject(new SessionRefreshError('Sessão terminada.', true));
  const renew = async (): Promise<RenewedSession> => {
    const currentRefresh = read(refreshKeys);
    if (!currentRefresh) throw new SessionRefreshError('Sessão terminada.', true);
    if (currentRefresh !== initialRefresh) {
      const access = read(accessKeys);
      if (!access) throw new SessionRefreshError('Sessão alterada.', true);
      return { access, refresh: currentRefresh };
    }
    const response = await fetch(`${CONFIG.BACKEND_URL}/api/auth/refresh/`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: currentRefresh }),
      signal: AbortSignal.timeout(10000),
    });
    if (!response.ok) throw new SessionRefreshError('Não foi possível renovar a sessão.', response.status === 401);
    const data = await response.json();
    if (typeof data.access !== 'string' || typeof data.refresh !== 'string')
      throw new SessionRefreshError('Resposta de sessão inválida.');
    if (read(refreshKeys) !== currentRefresh) throw new SessionRefreshError('A sessão mudou durante a renovação.');
    for (const key of accessKeys) localStorage.setItem(key, data.access);
    for (const key of refreshKeys) localStorage.setItem(key, data.refresh);
    localStorage.setItem('loginTimestamp', String(Date.now()));
    return { access: data.access, refresh: data.refresh };
  };
  inFlight = Promise.resolve(typeof navigator !== 'undefined' && navigator.locks
    ? navigator.locks.request('padelmaster-session-refresh', renew)
    : renew()).finally(() => { inFlight = null; });
  return inFlight;
}
